/**
 * Da periodo dashboard a intervallo di istanti — la finestra che web e mobile
 * mandano a `GET /api/energy/dashboard-summary`.
 *
 * Vive qui (e non nell'hook web) perché la stessa funzione la usa anche il
 * mobile: due copie della conversione periodo→intervallo resterebbero allineate
 * solo finché qualcuno se ne ricorda, e «oggi» sul mobile deve voler dire
 * esattamente gli stessi istanti che intende il web.
 *
 * Il calcolo è sul calendario **locale** di chi guarda: «oggi» è il suo oggi,
 * non quello UTC del server.
 */

export type DashboardPeriod = 'oggi' | 'ieri' | 'sette_giorni' | 'mese' | 'ultima_validazione';

/** I periodi in ordine di presentazione, con l'etichetta IT del selettore. */
export const DASHBOARD_PERIODS: { value: DashboardPeriod; label: string }[] = [
	{ value: 'oggi', label: 'Oggi' },
	{ value: 'ieri', label: 'Ieri' },
	{ value: 'sette_giorni', label: 'Ultimi 7 giorni' },
	{ value: 'mese', label: 'Mese in corso' },
	{ value: 'ultima_validazione', label: 'Ultima validazione' },
];

export interface DashboardSummaryRange {
	dayFrom: string;
	monthFrom: string;
	to: string;
}

/**
 * `dayFrom` delimita i totali di giornata e `monthFrom` quelli cumulati: sulle
 * finestre che non sono un giorno solo coincidono, perché il KPI «del periodo»
 * e quello «del mese» sono lo stesso numero.
 */
export function rangeForPeriod(
	period: DashboardPeriod,
	now: Date = new Date(),
): DashboardSummaryRange {
	const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
	const iso = (d: Date) => d.toISOString();
	const today = startOfDay(now);

	switch (period) {
		case 'ieri': {
			const yesterday = new Date(today.getTime() - 86_400_000);
			return { dayFrom: iso(yesterday), monthFrom: iso(yesterday), to: iso(today) };
		}
		case 'sette_giorni': {
			const from = new Date(today.getTime() - 6 * 86_400_000);
			return { dayFrom: iso(from), monthFrom: iso(from), to: iso(now) };
		}
		case 'ultima_validazione': {
			const firstOfThis = new Date(now.getFullYear(), now.getMonth(), 1);
			const firstOfPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
			return { dayFrom: iso(firstOfPrev), monthFrom: iso(firstOfPrev), to: iso(firstOfThis) };
		}
		case 'mese': {
			// «Mese in corso» è una finestra di più giorni come le altre, quindi
			// `dayFrom` scende al primo del mese. Finché stava nel ramo di «oggi»
			// mandava al server esattamente la stessa richiesta — stesso `dayFrom`,
			// stesso `monthFrom`, stesso `to` — e nessuna correzione lato API poteva
			// distinguere le due schede: quella del mese mostrava i KPI e la curva
			// della sola giornata di oggi sotto un titolo che prometteva il mese.
			const first = new Date(now.getFullYear(), now.getMonth(), 1);
			return { dayFrom: iso(first), monthFrom: iso(first), to: iso(now) };
		}
		case 'oggi':
		default:
			return {
				dayFrom: iso(today),
				monthFrom: iso(new Date(now.getFullYear(), now.getMonth(), 1)),
				to: iso(now),
			};
	}
}
